import React from 'react';
import { useTheme } from '../../../context/ThemeContext';
import { SkeletonProfileHeader } from '../../common/LoadingSkeleton';

/**
 * UserProfileHeader component for displaying user's basic information
 * Shows profile picture, name, role, company, and LinkedIn link
 * Optimized for both mobile and desktop viewing
 */
const UserProfileHeader = ({ profileData, loading = false }) => {
  const { isDarkMode } = useTheme();

  // Extract user data with fallbacks
  const {
    firstName = '',
    lastName = '',
    currentRole = '',
    companyName = '',
    photoURL = '',
    linkedinUrl = ''
  } = profileData?.basicInfo || {};

  // Generate initials for fallback avatar
  const getInitials = () => {
    const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
    const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
    return firstInitial + lastInitial || '?';
  };

  // Format LinkedIn URL to ensure it's a proper external link
  const formatLinkedInUrl = (url) => {
    if (!url) return '';
    
    // Add https:// prefix if missing
    if (!/^https?:\/\//i.test(url)) {
      return `https://${url}`;
    }
    
    return url;
  };

  const fullName = `${firstName} ${lastName}`.trim();
  const formattedLinkedInUrl = formatLinkedInUrl(linkedinUrl);

  if (loading) {
    return <SkeletonProfileHeader className="mb-6" />;
  }

  return (
    <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-4 sm:p-6 mb-6`}>
      <div className="flex flex-col items-center text-center">
        {/* Profile Picture */}
        <div className="mb-4">
          {photoURL ? (
            <img
              src={photoURL}
              alt={`${fullName}'s profile`}
              className="w-20 h-20 sm:w-24 sm:h-24 lg:w-30 lg:h-30 rounded-full object-cover 
                       border-4 border-purple-100 dark:border-purple-800 
                       shadow-lg"
              onError={(e) => {
                // Fallback to initials if image fails to load
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          
          {/* Fallback avatar with initials */}
          <div 
            className={`w-20 h-20 sm:w-24 sm:h-24 lg:w-30 lg:h-30 rounded-full 
                       bg-white dark:bg-gray-800 
                       border-2 border-purple-500
                       flex items-center justify-center text-purple-600 dark:text-purple-400 font-semibold 
                       text-xl sm:text-2xl lg:text-3xl
                       shadow-lg ${
              photoURL ? 'hidden' : 'flex'
            }`}
          >
            {getInitials()}
          </div>
        </div>

        {/* User Name */}
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-2">
          {fullName || 'Unknown User'}
        </h1>

        {/* Current Role */}
        {currentRole && (
          <p className="text-lg sm:text-xl font-medium text-gray-600 dark:text-gray-300 mb-2">
            {currentRole}
          </p>
        )}

        {/* Company */}
        {companyName && (
          <div className="flex items-center justify-center mb-4">
            <svg 
              className="w-5 h-5 mr-2 text-gray-500 dark:text-gray-400" 
              fill="currentColor" 
              viewBox="0 0 20 20"
            >
              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
            </svg>
            <p className="text-base sm:text-lg text-gray-500 dark:text-gray-400">
              {companyName}
            </p>
          </div>
        )}

        {/* LinkedIn Link */}
        {formattedLinkedInUrl && (
          <div className="flex items-center justify-center">
            <a
              href={formattedLinkedInUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300
                       underline hover:no-underline transition-all duration-200 ease-out
                       focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 
                       dark:focus:ring-offset-gray-800 rounded px-1 py-1"
            >
              <svg 
                className="w-4 h-4 mr-1" 
                fill="currentColor" 
                viewBox="0 0 24 24"
              >
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
              LinkedIn Profile
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfileHeader;